import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as MdIcons from 'react-icons/md';
import { mixAudioFiles } from '../utils/audioMixer';

const { MdClose, MdCallMerge, MdCloudUpload, MdMusicNote, MdCheckCircle } = MdIcons;

const MiniUploader = ({ label, file, onFileSelect }) => {
  const inputRef = useRef(null);
  
  return (
    <div 
      onClick={() => inputRef.current?.click()}
      className={`relative p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all flex items-center justify-between
        ${file ? 'border-primary bg-primary/5' : 'border-slate-300 dark:border-border-dark hover:border-primary/50'}`}
    >
      <input 
        type="file" 
        accept="audio/*" 
        className="hidden" 
        ref={inputRef}
        onChange={(e) => {
          if (e.target.files[0]) onFileSelect(e.target.files[0]);
        }}
      />
      <div className="flex items-center gap-3 overflow-hidden">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${file ? 'bg-primary text-white' : 'bg-slate-200 dark:bg-surface-dark text-slate-500'}`}>
          <SafeIcon icon={file ? MdMusicNote : MdCloudUpload} className="text-xl" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-xs font-bold text-slate-500 dark:text-text-secondary uppercase tracking-wider">{label}</span>
          <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
            {file ? file.name : 'Click to select audio file'}
          </span>
        </div>
      </div>
      {file && <SafeIcon icon={MdCheckCircle} className="text-xl text-green-500 shrink-0 ml-2" />}
    </div>
  );
};

const MixerModal = ({ isOpen, onClose, initialTrack1, onMixComplete }) => {
  const [track1, setTrack1] = useState(initialTrack1 || null);
  const [track2, setTrack2] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (isOpen && initialTrack1) {
      setTrack1(initialTrack1);
    }
  }, [isOpen, initialTrack1]);

  const handleMix = async () => {
    if (!track1 || !track2) return;
    
    setIsProcessing(true);
    setError(null);
    setProgressText('Initializing...');

    try {
      // 3 seconds crossfade by default
      const mixedFile = await mixAudioFiles(track1, track2, 3, (status) => {
        setProgressText(status);
      });
      
      onMixComplete(mixedFile);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={!isProcessing ? onClose : undefined}
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg bg-white dark:bg-background-dark border border-slate-200 dark:border-border-dark rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-slate-200 dark:border-border-dark flex justify-between items-center bg-slate-50 dark:bg-surface-dark/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <SafeIcon icon={MdCallMerge} className="text-xl" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Mix 2 Songs</h3>
            </div>
            {!isProcessing && (
              <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 transition-colors">
                <SafeIcon icon={MdClose} className="text-xl" />
              </button>
            )}
          </div>

          <div className="p-6 space-y-6">
            <p className="text-sm text-slate-500 dark:text-text-secondary leading-relaxed">
              Combine two tracks into one seamless long file. A 3-second crossfade will be applied automatically to remove silence between tracks.
            </p>

            <div className="space-y-4 relative">
              <MiniUploader label="Track 1 (Starts First)" file={track1} onFileSelect={setTrack1} />
              
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white dark:bg-surface-dark border border-slate-200 dark:border-border-dark rounded-full flex items-center justify-center z-10 text-slate-400">
                <SafeIcon icon={MdCallMerge} />
              </div>

              <MiniUploader label="Track 2 (Plays After)" file={track2} onFileSelect={setTrack2} />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-sm font-medium border border-red-200 dark:border-red-500/20">
                {error}
              </div>
            )}

            {isProcessing && (
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-surface-dark border border-slate-200 dark:border-border-dark flex flex-col items-center gap-3">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{progressText}</p>
                <div className="w-full h-1.5 bg-slate-200 dark:bg-border-dark rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-primary"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-6 pt-0">
            <button 
              onClick={handleMix}
              disabled={!track1 || !track2 || isProcessing}
              className="w-full py-3.5 rounded-xl font-bold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-lg shadow-primary/25"
            >
              {isProcessing ? 'Processing Mix...' : 'Mix Tracks & Compress'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MixerModal;